import * as React from "react";
import MessageBubble from "./MessageBubble";
import { ConversationMessage } from "@lupp/shared";

export interface ConversationViewProps {
  messages: ConversationMessage[];
  isSending: boolean;
  onSend: (text: string) => void;
  onSwitchBranch?: (messageId: string, dir: -1 | 1) => void;
  onEditSave?: (messageId: string, text: string) => void;
  onEditResend?: (messageId: string, text: string) => void;
  onDeleteMessage?: (messageId: string) => void;
  onOpenProposals?: (messageId: string) => void;
  scrollTarget?: { id: string | null; seq: number };
}

interface ConversationViewState {
  showScrollBtn: boolean;
}

export default class ConversationView extends React.Component<ConversationViewProps, ConversationViewState> {
  private scrollRef = React.createRef<HTMLDivElement>();

  constructor(props) {
    super(props);
    this.state = { showScrollBtn: false };
  }

  componentDidMount(): void {
    this.scrollRef.current?.addEventListener("scroll", this.handleScroll, { passive: true });
    // Restored history may already be present on mount → start at the bottom.
    this.scrollToBottom();
  }

  componentWillUnmount(): void {
    this.scrollRef.current?.removeEventListener("scroll", this.handleScroll);
  }

  componentDidUpdate(prev: ConversationViewProps): void {
    // Explicit scroll request (branch switch / delete) wins over the bottom heuristic:
    // scroll to the targeted message (or to the bottom when id is null).
    if (this.props.scrollTarget && this.props.scrollTarget.seq !== prev.scrollTarget?.seq) {
      this.scrollToTarget(this.props.scrollTarget.id);
      return;
    }

    const lastPrev = prev.messages[prev.messages.length - 1];
    const lastCur = this.props.messages[this.props.messages.length - 1];
    // A loaded/restored chat replaces the whole list: length and/or the last message
    // id changes → always jump to the bottom (ignore the scroll-button guard, since the
    // user hasn't scrolled within this new conversation yet).
    const historyReplaced = prev.messages.length !== this.props.messages.length || lastPrev?.id !== lastCur?.id;
    const thinkingGrew = lastPrev?.thinkingSteps?.length !== lastCur?.thinkingSteps?.length;

    if (historyReplaced) {
      this.scrollToBottom();
    } else if (thinkingGrew || prev.isSending !== this.props.isSending) {
      if (!this.state.showScrollBtn) this.scrollToBottom();
    }
  }

  /** Scroll a specific message to the top of the view; fall back to the bottom. */
  scrollToTarget = (id: string | null): void => {
    if (!id) {
      this.scrollToBottom();
      return;
    }
    const scroll = (): void => {
      const el = this.scrollRef.current?.querySelector(`[data-message-id="${id}"]`) as HTMLElement | null;
      if (el) el.scrollIntoView({ block: "start" });
      else this.scrollToBottom();
    };
    scroll();
    if (typeof requestAnimationFrame === "function") requestAnimationFrame(scroll); // settle after layout
  };

  handleScroll = (): void => {
    const el = this.scrollRef.current;
    if (!el) return;
    const distFromBottom = el.scrollHeight - el.scrollTop - el.clientHeight;
    this.setState({ showScrollBtn: distFromBottom > 80 });
  };

  scrollToBottom = (): void => {
    const el = this.scrollRef.current;
    if (!el) return;
    el.scrollTop = el.scrollHeight;
    // Markdown, model avatars and thinking blocks can grow the content height after
    // React commits → settle on the next frame so we truly land at the bottom.
    if (typeof requestAnimationFrame === "function") {
      requestAnimationFrame(() => {
        const e2 = this.scrollRef.current;
        if (e2) e2.scrollTop = e2.scrollHeight;
      });
    }
  };

  render(): JSX.Element {
    const { messages, onSend, onSwitchBranch, onEditSave, onEditResend, onDeleteMessage, onOpenProposals } = this.props;
    const { showScrollBtn } = this.state;

    return (
      <div className="conversation-wrap">
        <div className="conversation" ref={this.scrollRef}>
          <div className="message-list">
            {messages.map((m, i) => (
              <MessageBubble key={m.id} message={m} isLast={i === messages.length - 1} onSend={onSend} onSwitchBranch={onSwitchBranch} onEditSave={onEditSave} onEditResend={onEditResend} onDeleteMessage={onDeleteMessage} onOpenProposals={onOpenProposals} />
            ))}
          </div>
        </div>

        {showScrollBtn && (
          <button className="scroll-btn" type="button" aria-label="Nach unten scrollen" onClick={this.scrollToBottom}>
            <svg
              width="16"
              height="16"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2.5"
              strokeLinecap="round"
              strokeLinejoin="round"
              aria-hidden="true"
            >
              <path d="M12 5v14M5 15l7 7 7-7" />
            </svg>
          </button>
        )}
      </div>
    );
  }
}
